import { useMemo } from "react";
import i18n from "locales/i18n";
import {
  useCancelMeetingMutation,
  useConfirmMeetingMutation,
} from "generated/graphql";
import { useVendorBookings } from "../calendar/booking/bookingsApi";
import { useLeads } from "../calendar/booking/leadSuggestions";
import {
  AttendeeStatus,
  Booking,
  LeadCustomer,
} from "../calendar/booking/types";
import {
  deriveDisplayStatus,
  defaultStatus,
  Meeting,
  MeetingEstimate,
  MeetingStatus,
} from "./types";

const tm = i18n.getFixedT(null, "meetings");
export interface ConfirmInput {
  meetingLink?: string;
  note?: string;
  // Optional new time (the "propose new time" path).
  date?: string;
  from?: string;
  to?: string;
}

export interface MeetingActionResult {
  ok: boolean;
  error?: string;
}

const primaryAttendee = (b: Booking) =>
  b.attendees.find((a) => !a.optional) ?? b.attendees[0];

// Prefer the booking's explicitly-linked estimate; fall back to inferring one
// from the customer's Leads (covers bookings created before a quote was linked).
const estimateOf = (
  b: Booking,
  customer?: LeadCustomer,
): MeetingEstimate | undefined => {
  if (b.quote) {
    return {
      quoteId: b.quote.id,
      estimateNumber: b.quote.estimateNumber,
      chargerModel: b.quote.chargerModel,
      totalCost: b.quote.totalCost,
      stage: b.quote.stage,
    };
  }
  const quote = customer?.quotes[0];
  return quote
    ? {
        quoteId: quote.id,
        estimateNumber: quote.estimateNumber ?? undefined,
        chargerModel: quote.chargerModel ?? undefined,
        totalCost: quote.totalCost ?? undefined,
        stage: quote.stage ?? undefined,
      }
    : undefined;
};

const toMeeting = (b: Booking, byEmail: Map<string, LeadCustomer>): Meeting => {
  const attendee = primaryAttendee(b);
  const email = attendee?.email;
  const customer = email ? byEmail.get(email) : undefined;

  const status: MeetingStatus =
    (b.status as MeetingStatus) ?? defaultStatus(b.type);
  const customerResponse = attendee?.status as AttendeeStatus | undefined;

  return {
    id: b.id,
    booking: b,
    title: b.title,
    type: b.type,
    date: b.date,
    from: b.from,
    to: b.to,
    allDay: b.allDay,
    customerName: customer?.name || attendee?.name || email || "—",
    customerEmail: email,
    customerPhone: customer?.phone,
    status,
    displayStatus: deriveDisplayStatus(
      status,
      b.date,
      b.to,
      customerResponse,
      !!attendee,
    ),
    meetingLink: b.meetingLink,
    vendorNote: b.vendorNote,
    estimate: estimateOf(b, customer),
    customerResponse,
    proposedDate: attendee?.proposedDate,
    proposedFrom: attendee?.proposedFrom,
    proposedTo: attendee?.proposedTo,
    createdAt: b.createdAt,
  };
};

export const useMeetings = (isVendor: boolean) => {
  const { bookings, loading, remove } = useVendorBookings(isVendor);
  const { byEmail } = useLeads(isVendor);
  const [, confirmMut] = useConfirmMeetingMutation();
  const [, cancelMut] = useCancelMeetingMutation();

  const meetings = useMemo(
    () =>
      bookings
        .filter((b) => b.type !== "blocked")
        .map((b) => toMeeting(b, byEmail)),
    [bookings, byEmail],
  );

  // Confirm a meeting (optionally with a new proposed time); the customer is
  // emailed the join link. urql graphcache updates the normalized Booking.
  const confirm = async (
    id: string,
    input: ConfirmInput,
  ): Promise<MeetingActionResult> => {
    const res = await confirmMut({
      bookingId: Number(id),
      meetingLink: input.meetingLink,
      note: input.note,
      newDate: input.date,
      newFrom: input.from,
      newTo: input.to,
    });
    const payload = res.data?.confirmMeeting;
    if (res.error || (payload?.errors && payload.errors.length > 0)) {
      return {
        ok: false,
        error:
          payload?.errors?.[0]?.message ??
          res.error?.message ??
          tm("snack.confirm_failed"),
      };
    }
    return { ok: true };
  };

  const cancel = async (
    id: string,
    note?: string,
  ): Promise<MeetingActionResult> => {
    const res = await cancelMut({ bookingId: Number(id), note });
    const payload = res.data?.cancelMeeting;
    if (res.error || (payload?.errors && payload.errors.length > 0)) {
      return {
        ok: false,
        error:
          payload?.errors?.[0]?.message ??
          res.error?.message ??
          tm("snack.cancel_failed"),
      };
    }
    return { ok: true };
  };

  return { meetings, loading, confirm, cancel, remove, byEmail };
};
